import { useEffect, useMemo, useState } from "react"
import { useAppSelector } from "integrations/redux/hooks"
import {
    selectWeatherEnabled,
    selectWeatherPanel,
    selectWeatherTimeline,
} from "features/map/control/selectors"
import { WeatherMode } from "features/map/control/types"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useSettledView } from "../shared/weatherViewport"
import type { WeatherFrame, WeatherTileSource } from "./weatherTileSource"
import {
    MANIFEST_POLL_MS,
    WeatherFetchError,
    useWeatherManifestGeneration,
} from "./weatherTileSource"
import {
    abortInFlightFrames,
    cacheFrameBudget,
    cacheSet,
    clearInFlightFrame,
    dropFrames,
    useCacheGeneration,
    getCachedFrame,
    frameKey,
    markFrameFailed,
    needsFetch,
    registerInFlightFrame,
    setPinnedFrames,
} from "./weatherFrameCache"
import { FRAME_BURST, bracketHours, computeFetchWindow, frameCost } from "./weatherWindow"
import { useWeatherTimelineWindow } from "./weatherTimelineWindow"
import { publishWeatherFrameTimes } from "./weatherFrameIndex"
import { bracket, displayedFrameIndex } from "./frameResolution"
import type { FrameImage } from "../shared/frameTextureCache"

export type WeatherSeries = {
    frames: WeatherFrame[]
    bounds: [number, number, number, number]
    hasCoverage: boolean
}

export const useWeatherSeries = (
    mode: WeatherMode,
    source: WeatherTileSource,
    enabled: boolean = true
) => {
    const weather = useAppSelector(selectWeatherEnabled)
    const { mode: panelMode } = useAppSelector(selectWeatherPanel)
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const { currentTime, isPlaying } = useAppSelector(selectWeatherTimeline)
    const { startMs: extentStart, endMs: extentEnd } = useWeatherTimelineWindow(mode)
    const [displayed, setDisplayed] = useState<WeatherSeries | null>(null)

    const active = weather && panelMode === mode && enabled

    const settledBbox = useSettledView(active)?.bbox ?? null
    const view = useMemo(
        () => (settledBbox ? source.view(settledBbox) : null),
        [settledBbox, source]
    )

    const generation = useCacheGeneration()
    const manifestGen = useWeatherManifestGeneration()

    const frameBudget = view ? cacheFrameBudget(view.frameBytes) : 0
    const strideMs = useMemo(
        () => source.frameStrideMs(extentStart, extentEnd, frameBudget),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [source, extentStart, extentEnd, frameBudget, manifestGen]
    )

    useEffect(() => {
        if (!active || !workspaceId || !settledBbox || !view) return
        const bk = view.key
        const now = Date.now()
        const t = Date.parse(currentTime)
        const { winStart, winEnd } = computeFetchWindow(mode, t, isPlaying, extentStart, extentEnd)
        if (winEnd <= winStart) return

        const prefix = `${mode}|${bk}|`
        abortInFlightFrames(prefix, (hourMs) => hourMs < winStart || hourMs > winEnd)

        void source.ensureFrameIndex()
        const winHours = source.frameTimes(winStart, winEnd, strideMs)
        if (winHours.length === 0) return

        // Asking for more frames than the cache holds makes every write evict one the next pass
        // wants back, and the eviction bumps the generation this effect listens on.
        const affordable = [...winHours]
            .sort((a, b) => frameCost(a, t, isPlaying) - frameCost(b, t, isPlaying))
            .slice(0, frameBudget)

        const stale = (h: number) => needsFetch(mode, bk, h, now, source.frameVersion(h))
        const bracketPending = bracketHours(winHours, t).filter(stale)
        const isBracketPass = bracketPending.length > 0
        const pending = isBracketPass
            ? bracketPending
            : affordable.filter(stale).slice(0, FRAME_BURST)
        if (pending.length === 0) return

        const fetches = pending.map((hourMs) => {
            const key = frameKey(mode, bk, hourMs)
            const version = source.frameVersion(hourMs)
            const controller = new AbortController()
            const priority = isBracketPass ? 1 : -frameCost(hourMs, t, isPlaying)
            const p = source
                .fetchFrame(
                    settledBbox,
                    new Date(hourMs).toISOString(),
                    priority,
                    controller.signal
                )
                .then((frame) => cacheSet(key, frame, version))
                .catch((err) => {
                    if (controller.signal.aborted) return
                    markFrameFailed(key)
                    const transient =
                        err instanceof WeatherFetchError &&
                        (err.status >= 500 || err.status === 429)
                    if (transient) {
                        console.warn(
                            `[weather:${mode}] tile temporarily unavailable (${err.status})`
                        )
                    } else {
                        console.error(`[weather:${mode}] frame fetch failed:`, err)
                    }
                })
                .finally(() => clearInFlightFrame(key))
            registerInFlightFrame(key, p, controller)
            return p
        })
        void Promise.all(fetches)
    }, [
        active,
        workspaceId,
        settledBbox,
        view,
        currentTime,
        isPlaying,
        extentStart,
        extentEnd,
        mode,
        source,
        strideMs,
        frameBudget,
        generation,
        manifestGen,
    ])

    useEffect(() => {
        if (!active || !view) return undefined
        const prefix = `${mode}|${view.key}|`
        return () => {
            abortInFlightFrames(prefix)
            dropFrames(prefix)
        }
    }, [active, view, mode])

    useEffect(() => {
        if (!active) return undefined
        const id = setInterval(() => void source.ensureFrameIndex(), MANIFEST_POLL_MS)
        return () => clearInterval(id)
    }, [active, source])

    const mergedNow = useMemo(() => {
        if (!active || !view) return null
        let anyCached = false
        const frames: WeatherFrame[] = []
        for (const h of source.frameTimes(extentStart, extentEnd, strideMs)) {
            const cached = getCachedFrame(frameKey(mode, view.key, h))
            if (cached === undefined) continue
            anyCached = true
            if (cached) frames.push(cached)
        }
        const series = { frames, bounds: view.bounds, hasCoverage: frames.length > 0 }
        return { anyCached, series }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, view, extentStart, extentEnd, mode, source, strideMs, generation, manifestGen])

    useEffect(() => {
        if (!mergedNow) return
        // Published unconditionally, including empty: the index describes the settled view, so a pan
        // into uncached territory has to clear it rather than leave the previous bbox's hours up.
        publishWeatherFrameTimes(
            mode,
            mergedNow.series.frames.map((f) => Date.parse(f.timestamp))
        )
        if (!mergedNow.anyCached) return
        setPinnedFrames(mode, mergedNow.series.frames)
        setDisplayed(mergedNow.series)
    }, [mergedNow, mode])

    useEffect(() => {
        if (active) return () => setPinnedFrames(mode, [])
        setPinnedFrames(mode, [])
        setDisplayed(null)
        publishWeatherFrameTimes(mode, [])
        return undefined
    }, [active, mode])

    if (!active) return null
    return displayed
}

export type DisplayFrame = {
    imageA: FrameImage
    imageB: FrameImage
    imageWeight: number
    bounds: [number, number, number, number]
    timestamp: string
}

const MODE_BLEND: Record<WeatherMode, "interpolate" | "snap"> = {
    wind: "interpolate",
    precipitation: "interpolate",
    lightning: "interpolate",
    radar: "snap",
}

export const useDisplayFrame = (
    mode: WeatherMode,
    series: WeatherSeries | null,
    currentTime: string
) => {
    const framesMs = useMemo(
        () => series?.frames.map((f) => Date.parse(f.timestamp)) ?? [],
        [series]
    )
    return useMemo(() => {
        if (!series || series.frames.length === 0) return null
        const targetMs = Date.parse(currentTime)
        const displayed = displayedFrameIndex(framesMs, targetMs)
        if (displayed === null) return null

        const asDisplayed = (): DisplayFrame => ({
            imageA: series.frames[displayed].image,
            imageB: series.frames[displayed].image,
            imageWeight: 0,
            bounds: series.bounds,
            timestamp: series.frames[displayed].timestamp,
        })
        if (MODE_BLEND[mode] === "snap") return asDisplayed()

        const { before, after } = bracket(framesMs, targetMs)
        if (before === after) return asDisplayed()
        return {
            imageA: series.frames[before].image,
            imageB: series.frames[after].image,
            imageWeight: (targetMs - framesMs[before]) / (framesMs[after] - framesMs[before]),
            bounds: series.bounds,
            timestamp: series.frames[displayed].timestamp,
        }
    }, [mode, series, framesMs, currentTime])
}
